// Cliente de Shorts Studio: re-estiliza el video del usuario con una plantilla (Gemini Omni).
// Feature NUEVA, solo backend de Atlas (no tiene versión n8n). Mismo patrón async en 2 pasos
// que la edición de video (lib/omniEdit.ts):
//   1) START → dispara el job `video_shorts` y devuelve un jobId.
//   2) STATUS → se polea hasta `done` (con el storage path) o `error`.
// El backend hace split en chunks ≤10s → Omni por chunk (con frame de referencia) → stitch +
// re-mux del audio original. Puede tardar varios minutos (chunks en serie). Ver spec.
import { supabase } from './supabase';
import { atlasBackendFetch } from './atlasBackend';

export type ShortsStatus = 'processing' | 'done' | 'error';

export interface ShortsParams {
  projectId: string;
  /** Signed URL (descargable) del video subido por el usuario. */
  sourceUrl: string;
  /** Prompt de la plantilla elegida (define el estilo). */
  templatePrompt: string;
  /** 9:16 por defecto (vertical). */
  aspectRatio?: string;
  /** Duración del video en segundos (define el costo: 1 cr/s). */
  durationSec: number;
}

export interface ShortsResult {
  jobId: string;
  /** Storage path del short generado dentro del bucket `video-project-assets`. */
  path: string;
  /** Signed URL lista para reproducir el resultado. */
  signedUrl: string;
}

const BUCKET = 'video-project-assets';
const POLL_INTERVAL_MS = 4000;
// Shorts procesa N chunks en serie (~90s c/u) → margen amplio: 180 × 4s = ~12 min.
const POLL_MAX_ATTEMPTS = 180;

function sleep(ms: number): Promise<void> {
  return new Promise((r) => window.setTimeout(r, ms));
}

/**
 * Costo en créditos: 1 por segundo del video total, mínimo 1 (espejo del backend
 * `shortsCredits`). Cada segundo se procesa una sola vez, así que el costo ≈ duración total.
 */
export function creditsForShort(durationSec: number): number {
  return Math.max(1, Math.ceil(durationSec));
}

/**
 * Dispara la generación del short y polea hasta que termina. Devuelve el path + signed URL
 * del video estilizado. Lanza Error si falla o hace timeout. `onStatus` reporta transiciones.
 */
export async function generarShort(
  params: ShortsParams,
  onStatus?: (s: ShortsStatus) => void,
): Promise<ShortsResult> {
  // El tenant (user/company) lo saca el backend del JWT — el body NO lo lleva.
  const startBody = {
    project_id: params.projectId,
    source_url: params.sourceUrl,
    template_prompt: params.templatePrompt,
    aspect_ratio: params.aspectRatio ?? '9:16',
    duration_sec: params.durationSec,
  };

  // ── 1) START ──────────────────────────────────────────────────────────
  const startRes = await atlasBackendFetch('/v1/videos/shorts/start', startBody);
  if (!startRes.ok) {
    throw new Error(`No se pudo iniciar la generación (${startRes.status}).`);
  }
  const startRaw = await startRes.text();
  let startJson: { response?: string; jobId?: string; error?: string };
  try {
    startJson = JSON.parse(startRaw);
  } catch {
    throw new Error('Respuesta inválida del servidor.');
  }
  if (startJson.response !== 'success' || !startJson.jobId) {
    throw new Error(startJson.error || 'No se pudo iniciar la generación.');
  }
  const jobId = startJson.jobId;
  onStatus?.('processing');

  // ── 2) POLL STATUS ────────────────────────────────────────────────────
  for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
    await sleep(POLL_INTERVAL_MS);
    let st: { status?: string; path?: string; error?: string };
    try {
      const stRes = await atlasBackendFetch('/v1/videos/shorts/status', { job_id: jobId });
      if (!stRes.ok) continue; // reintentar el poll
      st = await stRes.json();
    } catch {
      continue; // error de red puntual → reintentar
    }

    if (st.status === 'done' && st.path) {
      const { data: urlData } = await supabase.storage.from(BUCKET).createSignedUrl(st.path, 3600);
      if (!urlData?.signedUrl) {
        throw new Error('Short generado pero no se pudo obtener la URL.');
      }
      onStatus?.('done');
      return { jobId, path: st.path, signedUrl: urlData.signedUrl };
    }
    if (st.status === 'error') {
      onStatus?.('error');
      throw new Error(st.error || 'La generación del short falló.');
    }
    // status === 'processing' → seguir poleando
  }

  throw new Error('La generación tardó demasiado. Reintentá.');
}
